import { useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ImagePlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, checkImageFile, errorMessage } from "@/lib/api";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const Slot = ({ kind, label, hint, src }) => {
  const qc = useQueryClient();
  const ref = useRef(null);
  const run = async (fn, msg) => {
    try { qc.setQueryData(["settings"], await fn()); toast.success(msg); } catch (e) { toast.error(errorMessage(e)); }
  };
  const pick = (file) => {
    const bad = file && checkImageFile(file);
    if (bad) { toast.error(bad); return; }
    if (file) run(() => adminApi.uploadSetting(kind, file), `${label} updated`);
  };
  return (
    <div className="rounded-2xl border border-[#E5E0D8] bg-white p-5" data-testid={`site-image-${kind}`}>
      <p className="font-display text-xl font-semibold text-[#1B4332]">{label}</p>
      <p className="text-sm text-[#5B625D]">{hint}</p>
      <div className={`mt-4 grid place-items-center overflow-hidden rounded-xl bg-[#F4EEE2] ${kind === "logo" ? "h-32 w-32" : "aspect-[4/3] w-full"}`}>
        {src ? <img src={src} alt={label} className={`h-full w-full ${kind === "logo" ? "object-contain" : "object-cover"}`} /> : <span className="text-xs text-[#5B625D]">Not uploaded</span>}
      </div>
      <div className="mt-4 flex gap-2">
        <button onClick={() => ref.current?.click()} className="inline-flex h-10 items-center gap-2 rounded-full bg-[#1B4332] px-4 text-sm font-semibold text-white" data-testid={`site-image-${kind}-upload-btn`}><ImagePlus className="h-4 w-4" /> Upload</button>
        {src && <button onClick={() => run(() => adminApi.deleteSetting(kind), `${label} removed`)} className="inline-flex h-10 items-center gap-2 rounded-full border border-[#E5E0D8] px-4 text-sm font-semibold" data-testid={`site-image-${kind}-remove-btn`}><Trash2 className="h-4 w-4" /> Remove</button>}
      </div>
      <input ref={ref} type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={(e) => { pick(e.target.files[0]); e.target.value = ""; }} data-testid={`site-image-${kind}-input`} />
    </div>
  );
};

export const SiteImages = () => {
  const { logo, hero } = useSiteSettings();
  return (
    <div className="grid gap-4 md:grid-cols-[280px_1fr]">
      <Slot kind="logo" label="Store logo" hint="Shown at the top right of the website. Square PNG works best." src={logo} />
      <Slot kind="hero" label="Shop photo (hero)" hint="Main photo on the homepage. Use a real photo of your shop." src={hero} />
    </div>
  );
};
